import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ToastKind = 'success' | 'error';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  toasts: Toast[];
  success: (message: string) => void;
  error: (message: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const SUCCESS_TIMEOUT_MS = 4500;

let nextId = 1;

/**
 * App-wide toast/alert stack. Mounted once near the app root (above the
 * router, so a toast fired right before `navigate(...)` survives the route
 * change). Success toasts auto-dismiss; error toasts stay until the user
 * closes them, since missing why something failed is worse than an extra
 * click.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = nextId++;
    setToasts((ts) => [...ts, { id, kind, message }]);
    return id;
  }, []);

  const success = useCallback((message: string) => push('success', message), [push]);
  const error = useCallback((message: string) => push('error', message), [push]);

  const value = useMemo(
    () => ({ toasts, success, error, dismiss }),
    [toasts, success, error, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

/** Returns `{ success, error }` for firing a toast after a mutation settles. */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return { success: ctx.success, error: ctx.error };
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (toast.kind !== 'success') return;
    timerRef.current = setTimeout(() => onDismiss(toast.id), SUCCESS_TIMEOUT_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, toast.kind, onDismiss]);

  return (
    <div
      className={['ne-toast', `ne-toast--${toast.kind}`].join(' ')}
      role={toast.kind === 'error' ? 'alert' : 'status'}
    >
      <span className="ne-toast__message">{toast.message}</span>
      <button
        type="button"
        className="ne-toast__close"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
      >
        ✕
      </button>
    </div>
  );
}

/** Renders the active toast stack. Mount exactly once, near the app root. */
export function ToastViewport() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('ToastViewport must be used within a ToastProvider');
  const { toasts, dismiss } = ctx;

  if (toasts.length === 0) return null;

  return (
    <div className="ne-toast-viewport" aria-live="polite">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}
